import { positionKey } from "../chess/positionKey.ts";
import type { LessonCourse, LessonMove, MoveAssessment } from "../lessons/schema.ts";
import type { BookAlternative } from "./bookCoaching.ts";

/**
 * How a move stands in the book, judged from the authored course graph and
 * the coaching notes alone. The engine's rank of the same move is a separate
 * verdict and never changes this one.
 *
 * - course: the move this course teaches here (or its main-line reply).
 * - transposition: a course position reached by another move order.
 * - variation: a sound road the book names but this course does not follow.
 * - warning: a move the course flags as a mistake or a trap.
 * - outside: a move the book does not cover here; not wrong by itself.
 * - beyond: the prepared line has ended; the book has nothing to say.
 * - unchecked: there is no book evidence available to classify this move.
 */
export type OpeningPathKind =
  "course" | "transposition" | "variation" | "warning" | "outside" | "beyond" | "unchecked";

/** Authored reasons and illustrative replies for a particular decision. */
export type OpeningPathCoaching = {
  why: string;
  plan?: string;
  tradeoff?: string;
  watch?: string;
  /** An illustration from the decision position, not a forced continuation. */
  line?: string[];
};

/** One sound road the book knew at a position. */
export type OpeningPathRoad = {
  san: string;
  /** The named line or opening the move starts, when the book names it. */
  name: string | null;
  /**
   * The course's own move, an authored side line in the graph, or an
   * alternative from the coaching notes (often another opening, at move one).
   */
  kind: "course" | "variation" | "opening";
  coaching?: OpeningPathCoaching;
};

export type OpeningPathMove = {
  kind: OpeningPathKind;
  /** The named line the move belongs to, when the book names one. */
  name: string | null;
  /** The ECO code of the position reached, when the course records one. */
  eco: string | null;
  /** The name of the position reached ("Giuoco Piano"), when the book names it. */
  reached: string | null;
  /**
   * Every sound road the book knew at the position before this move, the
   * played move among them when it is one. Mistakes and traps are not roads.
   */
  roads: OpeningPathRoad[];
  /** This book move is the last prepared move of its line. */
  closesBook?: boolean;
  /** The explanation of the move played, matched to its actual position. */
  coaching?: OpeningPathCoaching;
};

export const ON_BOOK: ReadonlySet<OpeningPathKind> = new Set([
  "course",
  "transposition",
  "variation",
]);

export function onBook(path: OpeningPathMove | null | undefined): boolean {
  return Boolean(path && ON_BOOK.has(path.kind));
}

/** Labels that say nothing a reader can use ("Course line"). */
const PLAIN_NAMES = new Set(["Course line", "Course variation", "Reviewed continuation"]);

/** A road or line name worth showing, without the "Reviewed continuation ·" prefix. */
export function cleanName(name: string | null | undefined): string | null {
  const trimmed = (name || "").replace(/^Reviewed continuation ·\s*/, "").trim();
  return trimmed && !PLAIN_NAMES.has(trimmed) ? trimmed : null;
}

function roadName(course: LessonCourse, move: LessonMove): string | null {
  return cleanName(move.variationName || course.nodes[move.nextPositionKey]?.variationName);
}

/** The sound roads the book knew at a position: course edges first, named openings after. */
export function roadsAt(
  course: LessonCourse | null,
  fen: string,
  options: BookAlternative[] = []
): OpeningPathRoad[] {
  const node = course?.nodes[positionKey(fen)];
  const roads: OpeningPathRoad[] = [];
  for (const move of node?.moves || []) {
    if (move.role === "mistake" || move.role === "handoff" || move.tacticalWarning) continue;
    roads.push({
      san: move.san,
      name: course ? roadName(course, move) : null,
      kind: move.role === "sideline" ? "variation" : "course",
      coaching: {
        why: move.explanation,
        plan: move.next,
        tradeoff: move.tradeoff,
        watch: move.watch,
      },
    });
  }
  for (const option of options) {
    const flagged = node?.moves.some(
      (move) => move.san === option.san && (move.role === "mistake" || move.tacticalWarning)
    );
    if (flagged) continue;
    const existing = roads.find((road) => road.san === option.san);
    if (existing) {
      if (!existing.name) existing.name = cleanName(option.name);
      existing.coaching = {
        ...existing.coaching,
        why:
          option.why.length > (existing.coaching?.why.length || 0)
            ? option.why
            : existing.coaching!.why,
        watch: option.watch || existing.coaching?.watch,
        line: option.line,
      };
      continue;
    }
    roads.push({
      san: option.san,
      name: cleanName(option.name),
      kind: "opening",
      coaching: { why: option.why, watch: option.watch, line: option.line },
    });
  }
  return roads;
}

/** Record authored evidence at the actual position, independently of engine rank. */
export function openingPathForMove(args: {
  course: LessonCourse | null;
  fen: string;
  uci: string;
  san?: string;
  byStudent: boolean;
  assessment?: MoveAssessment | null;
  options?: BookAlternative[];
  followedVariation?: string | null;
  coaching?: OpeningPathCoaching;
  /** The prepared line had already ended before this move (free play, a handoff passed). */
  beyond?: boolean;
}): OpeningPathMove {
  const node = args.course?.nodes[positionKey(args.fen)];
  const edge = node?.moves.find((move) => move.uci === args.uci) || null;
  const after = edge ? args.course?.nodes[edge.nextPositionKey] : undefined;
  const roads = roadsAt(args.course, args.fen, args.options);
  const book = args.assessment?.book;
  const reached = cleanName(after?.variationName);
  const eco = after?.eco || null;
  const name =
    cleanName(edge?.variationName) ||
    cleanName(args.followedVariation) ||
    cleanName(book?.variation) ||
    (edge ? reached : null);
  const playedSan = args.san || edge?.san;
  const coaching = roads.find((road) => road.san === playedSan)?.coaching || args.coaching;
  const base = { name, eco, reached, roads, coaching };
  // A known endpoint establishes the boundary; an unknown position alone does not.
  // A position with only side-line moves still has roads.
  const lineEnded = Boolean(args.beyond || node?.handoff || (node && node.moves.length === 0));
  // The move that reaches a handoff, or the end of a course line, completes it.
  const closes = (courseLine: boolean) =>
    Boolean(!lineEnded && after && (after.handoff || (courseLine && after.moves.length === 0)));

  if (edge?.role === "mistake" || edge?.tacticalWarning || book?.status === "warned") {
    // A prepared trap is covered by the lesson, but the bad move is not a sound alternative.
    return { ...base, kind: "warning" };
  }
  if (edge?.role === "transposition" || book?.status === "transposition")
    return { ...base, kind: "transposition", closesBook: closes(true) };
  if (edge?.role === "repertoire" || edge?.role === "mainline" || book?.status === "repertoire")
    return { ...base, kind: "course", closesBook: closes(true) };
  if (
    edge?.role === "sideline" ||
    args.followedVariation ||
    args.options?.some((option) => option.san === playedSan) ||
    book?.status === "known"
  )
    return {
      ...base,
      kind: "variation",
      name: name || roads.find((r) => r.san === playedSan)?.name || null,
      closesBook: closes(false),
    };
  if (edge) return { ...base, kind: "course", closesBook: closes(true) };
  if (!args.course && (!book || book.status === "unknown")) return { ...base, kind: "unchecked" };
  return { ...base, kind: lineEnded ? "beyond" : "outside" };
}
